/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import { useMsal } from "@azure/msal-react";

/**
 * Componente modal para creación y edición de departamentos.
 * 
 * Modal reutilizable que permite tanto crear nuevos departamentos
 * como editar existentes. Se integra con Microsoft Entra para
 * autenticación y realiza llamadas a la API REST.
 */

const ModalDepartamento = ({ show, onClose, onSave, departamento }) => {
  /* ============================
      Estado del formulario
     ============================ */
  const [nombre, setNombre] = useState("");

  /* ============================
      Autenticación (MSAL)
     ============================ */
  const { instance, accounts } = useMsal();

 /**
   * Obtiene token de acceso para autenticar peticiones API.
   */
  const getAccessToken = async () => {
    if (!accounts || accounts.length === 0) return null;

    try {
      const response = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });

      return response.accessToken;
    } catch (error) {
      console.error("Error obteniendo token:", error);
      return null;
    }
  };

  /* ============================
     Efecto: inicializar formulario
     ============================ */
  
  /**
   * Inicializa los campos del formulario según modo (crear/editar).
   * Si hay departamento prop, carga sus datos; si no, limpia formulario.
   */
  useEffect(() => {
    if (departamento) {
      setNombre(departamento.nombre || "");
    } else {
      setNombre("");
    }
  }, [departamento, show]);

  /* ============================
     Manejo de envío del formulario
     ============================ */
  
  /**
   * Maneja el envío del formulario (crear o editar).
   * Realiza petición a la API según el modo.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = await getAccessToken();
    if (!token) {
      console.error("No se pudo obtener token");
      return;
    }

    // Estructura de datos para la API
    const data = {nombre};

    try {
      if (departamento) {
        // EDITAR
        await axios.put(
          `${process.env.REACT_APP_API_URL}/departamentos/${departamento.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // CREAR
        await axios.post(
          `${process.env.REACT_APP_API_URL}/departamentos`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSave(); // Actualiza la lista del padre
      onClose();
    } catch (err) {
      console.error("Error al guardar departamento:", err);
    }
  };

  /*************************************************************
   *  Renderizado del modal de departamento                    *
   *************************************************************/
  return (
    <Modal show={show} onHide={onClose}>
      <Form onSubmit={handleSubmit}>
        {/* Cabecera del modal con título dinámico */}
        <Modal.Header closeButton style={{ backgroundColor: "#3b82f6", color: "white" }}>
          <Modal.Title>{departamento ? "Editar Departamento" : "Crear Departamento"}</Modal.Title>
        </Modal.Header>

        {/* Cuerpo del modal con campos del formulario */}
        <Modal.Body>
          {/* Campo: Nombre del departamento */}
          <Form.Group className="mb-3">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ej: Informática"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
              maxLength={50}
            />
          </Form.Group>

          {/* Campo: Planta del departamento 
          <Form.Group className="mb-3">
            <Form.Label>Planta</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ej: 1ª"
              value={planta}
              onChange={(e) => setPlanta(e.target.value)}
              required
              maxLength={50}
            />
          </Form.Group>*/}

          {/* Campo: Selección de edificio 
          <Form.Group className="mb-3">
            <Form.Label>Edificio</Form.Label>
            <Form.Select
              value={edificioId}
              onChange={(e) => setEdificioId(e.target.value)}
              required
            >
              <option value="">Selecciona un edificio</option>
              {edificios.map((ed) => (
                <option key={ed.id} value={ed.id}>
                  {ed.nombre}
                </option>
              ))}
            </Form.Select>
          </Form.Group>*/}
        </Modal.Body>
          
        {/* Estilos CSS-in-JS para el botón cerrar */}
        <style jsx>{`
          .modal-header .btn-close {
            filter: invert(1);
          }
        `}</style>

        {/* Pie del modal con botones de acción */}
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit">
            {departamento ? "Guardar cambios" : "Crear"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ModalDepartamento;
